import _ from "lodash";
import React, { useEffect } from "react";
import {useGet, usePost} from "../../common/hooks";
import {Division, Player, PostRequest, Team} from "../../common/interfaces";
import {Button, Card, Divider, Dropdown, Form, Input, List, Menu, message, Tabs} from "antd";
import {allKnownDivisions, getDivisionName} from "../../common/utils";
import {CompactTable} from "../../common/components/CompactTable";

const {TabPane} = Tabs;

function getMenu(
    personId: number,
    teams: Team[],
    assignPlayerToTeam: PostRequest
) {
  return (
      <Menu>
        {teams.map(team => (
            <Menu.Item
                key={team.id}
                onClick={() =>
                    assignPlayerToTeam.send({
                      personId: personId,
                      teamId: team.id,
                      isCaptain: false
                    })
                }
            >
              {team.name}
            </Menu.Item>
        ))}
      </Menu>
  );
}

function getDivisionTab(
    teams: Team[],
    players: Player[],
    assignPlayerToTeam: PostRequest,
    removePlayerFromTeam: PostRequest,
    division: Division
) {
  const playersInDivision = players.filter(p => p.division === division);
  const teamsInDivision = teams.filter(t => t.division === division);
  const playersWithoutTeam = playersInDivision.filter(p => !p.onTeam);

  return (
      <>
        {playersWithoutTeam.length > 0 && (
            <List
                dataSource={playersWithoutTeam}
                grid={{gutter: 16, column: 6}}
                renderItem={player => (
                    <List.Item>
                      <Dropdown
                          key={player.id}
                          overlay={getMenu(
                              player.id,
                              teamsInDivision,
                              assignPlayerToTeam
                          )}
                          placement="bottomLeft"
                      >
                        <p>
                          {player.firstName} {player.lastName}
                        </p>
                      </Dropdown>
                    </List.Item>
                )}
            />
        )}
        <List
            dataSource={teamsInDivision}
            grid={{gutter: 16, column: 6}}
            renderItem={team => (
                <List.Item>
                  <Card title={team.name}
                        extra={`${playersInDivision
                            .filter(p => p.teamId === team.id).length} players`}>
                    {playersInDivision
                        .filter(p => p.teamId === team.id)
                        .map((player: Player) => (
                            <Dropdown
                                key={player.id}
                                overlay={
                                  <Menu>
                                    <Menu.Item
                                        onClick={() =>
                                            removePlayerFromTeam.send({
                                              personId: player.id,
                                              teamId: team.id
                                            })
                                        }
                                    >
                                      Remove from {team.name}
                                    </Menu.Item>
                                    <Menu.Item
                                        onClick={() =>
                                            assignPlayerToTeam.send({
                                              personId: player.id,
                                              teamId: team.id,
                                              isCaptain: !player.isCaptain
                                            })
                                        }
                                    >
                                      {player.isCaptain
                                          ? "Remove captain"
                                          : "Assign captain"}
                                    </Menu.Item>
                                  </Menu>
                                }
                                placement="bottomLeft"
                            >
                              <p>
                                {player.firstName} {player.lastName}{player.isCaptain ? ' (C)' : ''}
                              </p>
                            </Dropdown>
                        ))}
                  </Card>
                </List.Item>
            )}
        />
      </>
  );
}

function TeamNameEdit({ team, onSaved }: { team: Team; onSaved: () => void }) {
  const [form] = Form.useForm();
  const updateTeamName = usePost({ uri: "/api/updateTeamName" });

  const displayId = `${getDivisionName(team.division)[0]}${team.teamId}`;

  useEffect(() => {
    const prefix = `${displayId} - `;
    const rawName = team.name.startsWith(prefix) ? team.name.slice(prefix.length) : "";
    form.setFieldsValue({ teamName: rawName });
  }, [team.name]);

  const onSubmit = () => {
    form.validateFields().then((values) => {
      updateTeamName.send({ ...values, teamId: team.id }, () => {
        message.success("Team name updated.");
        onSaved();
      });
    });
  };

  return (
    <Form form={form} layout="inline" onFinish={onSubmit} style={{ display: "flex", alignItems: "center" }}>
      <span style={{ marginRight: 4 }}>{displayId} -</span>
      <Form.Item name="teamName" style={{ marginBottom: 0 }}>
        <Input size="small" />
      </Form.Item>
      <Button htmlType="submit" loading={updateTeamName.isLoading} size="small">Save</Button>
    </Form>
  );
}

export function TeamsDashboard() {
  const allTeams = useGet<Team[]>("/api/teams");
  const createTeam = usePost({uri: "/api/createTeam", deps: [allTeams]});
  const allPlayers = useGet<Player[]>("/api/getPlayers");
  const assignPlayerToTeam = usePost({
    uri: "/api/addPlayerToTeam",
    deps: [allTeams, allPlayers]
  });
  const removePlayerFromTeam = usePost({
    uri: "/api/removePlayerFromTeam",
    deps: [allTeams, allPlayers]
  });
  const deleteTeam = usePost({uri: "/api/deleteTeam", deps: [allTeams]});

  const teamsColumns = [
    {title: "Name", render: (_: any, team: Team) => <TeamNameEdit team={team} onSaved={allTeams.reload} />},
    {dataIndex: "numPlayers", title: "# players"},
    {
      dataIndex: "usingJVite",
      title: "Using JVite?",
      render: (value: boolean) => (value ? "Yes" : "No")
    },
    {
      title: "",
      render: (_: any, team: Team) =>
        team.numPlayers === 0 ? (
          <Button
            danger
            size={"small"}
            loading={deleteTeam.isLoading}
            onClick={() => deleteTeam.send({teamId: team.id})}
          >
            Delete
          </Button>
        ) : null
    }
  ];

  const sortedTeams = _.sortBy(allTeams.data || [], ["division", "teamId"]);
  const sortedPlayers = _.sortBy(
      (allPlayers.data || [] as Player[]).filter(p => p.active),
      ["division", "lastName", "firstName"]);

  return (
      <>
        <Tabs defaultActiveKey="1">
          <TabPane tab="Current Teams" key="1">
            {allKnownDivisions.map((division: Division) => (
                <div key={division}>
                  <Divider>{getDivisionName(division)} Teams</Divider>
                  <Button
                      type={"primary"}
                      onClick={() => createTeam.send({divisionId: division})}
                  >
                    Add {getDivisionName(division)} Team
                  </Button>
                  <CompactTable
                      data={sortedTeams.filter(t => t.division === division)}
                      columns={teamsColumns}
                      rowKey={"id"}
                      loading={allTeams.isLoading}
                  />
                </div>
            ))}
          </TabPane>
          <TabPane tab="Green" key="2">
            {getDivisionTab(
                sortedTeams,
                sortedPlayers,
                assignPlayerToTeam,
                removePlayerFromTeam,
                Division.GREEN
            )}
          </TabPane>
          <TabPane tab="Red" key="3">
            {getDivisionTab(
                sortedTeams,
                sortedPlayers,
                assignPlayerToTeam,
                removePlayerFromTeam,
                Division.RED
            )}
          </TabPane>
          <TabPane tab="Maroon" key="4">
            {getDivisionTab(
                sortedTeams,
                sortedPlayers,
                assignPlayerToTeam,
                removePlayerFromTeam,
                Division.MAROON
            )}
          </TabPane>
          <TabPane tab="Blue" key="5">
            {getDivisionTab(
                sortedTeams,
                sortedPlayers,
                assignPlayerToTeam,
                removePlayerFromTeam,
                Division.BLUE
            )}
          </TabPane>
        </Tabs>
      </>
  );
}
